import { useEffect, useState } from "react"
import classNames from "classnames"
import Image from "next/image"
import { observer } from "mobx-react"
import { useStore } from "@features/common"
import { SOCIAL_POST_CLIPPATH_ID } from "@features/journal"
import { InstagramSerializer } from "@serializers/instagram"
import { IconLikes, IconViews, IconInstagram, IconCopy } from "@lib/icons"
import { Slider, SliderSlide } from "@ui"

const DEFAULT_INSTAGRAM_NAME = "@thepureapp"
const DEFAULT_INSTAGRAM_URL = "https://www.instagram.com/thepureapp/"

export const InstagramCarousel = observer(function InstagramCarousel() {
  const {
    BlogModel: { enabledInstagram, instagram },
    GlobalModel,
    ResponsiveModel,
  } = useStore()
  const [isWindows, setIsWindows] = useState<boolean>(false)

  useEffect(() => {
    setIsWindows(ResponsiveModel.isWindowsOS)
  }, [ResponsiveModel.isWindowsOS])

  if (!enabledInstagram || !instagram.length) {
    return null
  }

  const instagramURI =
    GlobalModel.getTemplate("instagram_link") || DEFAULT_INSTAGRAM_URL
  const instagramName =
    GlobalModel.getTemplate("instagram_name") || DEFAULT_INSTAGRAM_NAME

  const slidesCount = Math.min(Math.floor(ResponsiveModel.width / 320), 4)

  return (
    <section
      className="container px-0 pt-18 pb-23 tablet:pt-21 tablet:pb-25 laptop:px-22"
      data-test-id="instagram_section"
    >
      <div className="flex items-center px-4 mb-12 tablet:px-6 laptop:justify-center">
        <a
          href={instagramURI}
          target="_blank"
          className={classNames(
            "space-x-4 tablet:space-x-5 inline-flex text-2xl tablet:text-4xl uppercase tracking-2 transition-opacity duration-150 ease-in hover:opacity-60",
            isWindows ? "items-center" : "items-start",
          )}
          rel="noreferrer"
          data-test-id="instagram_section__title"
        >
          <span className="py-1">{instagramName}</span>
          <IconInstagram className="w-10 h-10" />
        </a>
      </div>
      {instagram.length > slidesCount ? (
        <Slider
          options={{
            spaceBetween: -16,
            slidesPerView: "auto",
            navigation: true,
            breakpoints: {
              600: {
                spaceBetween: -36,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 20,
              },
              1280: {
                slidesPerView: 3,
                spaceBetween: 102,
              },
              1440: {
                slidesPerView: 4,
                spaceBetween: 21,
              },
            },
          }}
        >
          {instagram.map((item, index) => (
            <SliderSlide
              key={`instagram-${index}`}
              className="w-auto flex justify-center px-4 tablet-s:px-6 laptop:px-0"
            >
              <div className="flex justify-center">
                <InstagramPreview data={item} />
              </div>
            </SliderSlide>
          ))}
        </Slider>
      ) : (
        <div className="flex justify-center space-x-4 tablet:space-x-10">
          {instagram.map((item) => (
            <InstagramPreview key={item.href} data={item} />
          ))}
        </div>
      )}
    </section>
  )
})

type InstagramPreviewProps = {
  data: ReturnType<typeof InstagramSerializer>
}

function InstagramPreview(props: InstagramPreviewProps) {
  const { data } = props

  return (
    <a
      href={data.href}
      target="_blank"
      className="relative group flex w-[300px] h-[300px] will-change-transform laptop:w-[263px] laptop:h-[263px] desktop:w-[300px] desktop:h-[300px]"
      rel="noreferrer"
      draggable={false}
      style={{
        clipPath: `url(#${SOCIAL_POST_CLIPPATH_ID})`,
      }}
      data-test-id="instagram_section__post"
    >
      <Image
        src={data.medium_image || data.original_image}
        objectFit="cover"
        layout="fill"
        unoptimized={true}
        draggable={false}
      />
      <IconCopy className="absolute z-2 top-4 right-4 w-5 h-5 " />
      <div className="hidden laptop:absolute laptop:inset-0 laptop:flex laptop:items-center laptop:justify-center laptop:text-lg laptop:text-white laptop:opacity-0 laptop:bg-black laptop:bg-opacity-40 laptop:cursor-pointer laptop:transition-opacity laptop:duration-100 laptop:ease-in group-hover:opacity-100">
        <div className="space-x-5 flex items-center justify-center">
          <div className="space-x-2 flex">
            <IconLikes className="w-5 h-5" />
            <span className="text-lg leading-6">{data.likes}</span>
          </div>
          <div className="space-x-2 flex">
            <IconViews className="w-5 h-5" />
            <span className="text-lg leading-6">{data.views}</span>
          </div>
        </div>
      </div>
    </a>
  )
}
